import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddParticipantModal } from './AddParticipantModal';
import { ConversationHeader } from './ConversationHeader';
import { MessageInput } from './MessageInput';
import { MessageList } from './MessageList';
import { ParticipantSidebar } from './ParticipantSidebar';
import { WhoReactedModal } from './WhoReactedModal';
import { useMessages, useSSE, useParticipants } from '../../hooks';
import { useReactionUsers } from '../../hooks/useReactionUsers';
import { createApiClient } from '../../lib/api-client';
import { useApiStore } from '../../store/api';
import { useAuthStore } from '../../store/auth';
import { useToastStore } from '../../store/toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface Conversation {
  id: string;
  name: string | null;
  status: 'active' | 'archived' | 'deleted';
  created_at: string;
  updated_at: string;
  last_activity_at: string;
}

interface ConversationDetailPageProps {
  conversation: Conversation;
  onUpdateName: (name: string) => Promise<void>;
}

export function ConversationDetailPage({
  conversation,
  onUpdateName,
}: ConversationDetailPageProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { apiUrl } = useApiStore();
  const addToast = useToastStore((s) => s.addToast);

  const {
    messages,
    loading: loadingMessages,
    sendMessage: sendMessageAPI,
    sendingMessage,
    editMessage: editMessageAPI,
    editingMessage,
    deleteMessage: deleteMessageAPI,
    deletingMessage,
    toggleReaction: toggleReactionAPI,
    refreshMessages,
  } = useMessages(conversation.id);

  const {
    participants,
    loading: loadingParticipants,
    removeParticipant: removeParticipantAPI,
    refreshParticipants,
  } = useParticipants(conversation.id);

  const { showWhoReacted, reactionUsers, loadingReactionUsers, loadWhoReacted, closeWhoReacted } =
    useReactionUsers();

  // UI state
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showParticipantPanel, setShowParticipantPanel] = useState(false);
  const [newMessageContent, setNewMessageContent] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const typingTimeouts = useRef<Map<string, number>>(new Map());
  const commonEmojis = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🚀', '👏'];

  useSSE({
    conversationId: conversation.id,
    onMessageCreated: () => refreshMessages(),
    onMessageUpdated: () => refreshMessages(),
    onMessageDeleted: () => refreshMessages(),
    onReactionAdded: () => refreshMessages(),
    onReactionRemoved: () => refreshMessages(),
    onTypingStarted: (username) => {
      setTypingUsers((prev) => new Set(prev).add(username));
      const existing = typingTimeouts.current.get(username);
      if (existing) clearTimeout(existing);
      const timeout = window.setTimeout(() => {
        setTypingUsers((prev) => {
          const next = new Set(prev);
          next.delete(username);
          return next;
        });
        typingTimeouts.current.delete(username);
      }, 5000);
      typingTimeouts.current.set(username, timeout);
    },
    onTypingEnded: (username) => {
      setTypingUsers((prev) => {
        const next = new Set(prev);
        next.delete(username);
        return next;
      });
    },
    onParticipantAdded: (participant) => {
      addToast(`${participant.username || participant.user_id} joined the conversation`, 'info');
      refreshParticipants();
    },
    onParticipantRemoved: (participant) => {
      addToast(`${participant.username || participant.user_id} left the conversation`, 'info');
      refreshParticipants();
    },
  });

  useEffect(() => {
    const timeouts = typingTimeouts.current;
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, []);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessageContent.trim()) return;
    try {
      await sendMessageAPI(newMessageContent.trim());
      setNewMessageContent('');
    } catch (err) {
      console.error('Failed to send message:', err);
      addToast('Failed to send message. Please try again.', 'error');
    }
  };

  const handleStartEdit = (message: { id: string; content: string }) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
  };

  const handleSaveEdit = async (messageId: string) => {
    if (!editContent.trim()) return;
    try {
      await editMessageAPI(messageId, editContent.trim());
      setEditingMessageId(null);
      setEditContent('');
    } catch (err) {
      console.error('Failed to edit message:', err);
      addToast('Failed to edit message. Please try again.', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleDeleteMessage = (messageId: string) => {
    setConfirmDeleteId(messageId);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setDeletingMessageId(confirmDeleteId);
      await deleteMessageAPI(confirmDeleteId);
    } catch (err) {
      console.error('Failed to delete message:', err);
      addToast('Failed to delete message. Please try again.', 'error');
    } finally {
      setDeletingMessageId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleToggleReaction = async (
    messageId: string,
    emoji: string,
    currentlyReacted: boolean
  ) => {
    try {
      await toggleReactionAPI(messageId, emoji, currentlyReacted);
    } catch (err) {
      console.error('Failed to toggle reaction:', err);
      addToast('Failed to update reaction.', 'error');
    }
  };

  const handleAddParticipant = async (username: string) => {
    try {
      const client = createApiClient(apiUrl);
      await client.POST('/v1/conversations/{id}/participants', {
        params: { path: { id: conversation.id } },
        body: { user_id: username.trim(), role: 'member' },
      });
      setShowAddParticipant(false);
      addToast(`Added ${username.trim()} to the conversation`, 'success');
      refreshParticipants();
    } catch (err) {
      console.error('Failed to add participant:', err);
      addToast('Failed to add participant. Please try again.', 'error');
    }
  };

  const handleRemoveParticipant = async (userId: string) => {
    try {
      await removeParticipantAPI(userId);
      addToast('Participant removed from the conversation', 'success');
    } catch (err) {
      console.error('Failed to remove participant:', err);
      addToast('Failed to remove participant. Please try again.', 'error');
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      data-testid="conversation-detail-view"
    >
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center h-16 gap-2">
            <button
              onClick={() => navigate('/conversations')}
              className="px-3 py-2 text-slate-300 hover:text-white transition-colors text-sm sm:text-base"
              data-testid="back-to-list-button"
              aria-label="Back to conversations"
            >
              &larr; Back
            </button>
          </div>
        </div>
      </header>

      <ConversationHeader
        conversation={conversation}
        participantCount={participants.length}
        onAddParticipant={() => setShowAddParticipant(!showAddParticipant)}
        onToggleParticipantPanel={() => setShowParticipantPanel(!showParticipantPanel)}
        onUpdateName={onUpdateName}
      />

      <AddParticipantModal
        show={showAddParticipant}
        onSubmit={handleAddParticipant}
        onClose={() => setShowAddParticipant(false)}
      />

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full">
          <MessageList
            messages={messages}
            loading={loadingMessages}
            currentUserId={user?.sub}
            editingMessageId={editingMessageId}
            editContent={editContent}
            savingEdit={editingMessage}
            onEditContentChange={setEditContent}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onDelete={handleDeleteMessage}
            onToggleReaction={handleToggleReaction}
            onLoadWhoReacted={(msgId, emoji) => loadWhoReacted(conversation.id, msgId, emoji)}
            deletingMessageId={deletingMessageId}
            typingUsers={typingUsers}
            commonEmojis={commonEmojis}
          />
          <MessageInput
            value={newMessageContent}
            onChange={setNewMessageContent}
            onSubmit={handleSendMessage}
            sending={sendingMessage}
          />
        </div>

        {showParticipantPanel && (
          <ParticipantSidebar
            participants={participants}
            loading={loadingParticipants}
            currentUserId={user?.sub}
            onRemoveParticipant={handleRemoveParticipant}
            onClose={() => setShowParticipantPanel(false)}
          />
        )}
      </div>

      {showWhoReacted && (
        <WhoReactedModal
          show={true}
          emoji={showWhoReacted.emoji}
          users={reactionUsers}
          loading={loadingReactionUsers}
          onClose={closeWhoReacted}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDeleteId !== null}
        title="Delete Message"
        message="Are you sure you want to delete this message? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        loading={deletingMessage}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}
